import { PAGE_LIMIT_MAX } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCanAnywhere, useCanWithoutBuilding } from "@/lib/policy";
import { EmployeeHrFile } from "./-components/employee-hr-file";
import { StartHiringFilesDialog } from "./-components/start-hiring-files-dialog";

export const Route = createFileRoute("/_authenticated/_workforce/hr/employees")({
  // The roster links straight to one person's file, which is how anybody
  // arrives here who did not come to browse.
  validateSearch: (search: Record<string, unknown>) => ({
    employeeId: typeof search.employeeId === "string" ? search.employeeId : undefined,
  }),
  component: HrEmployees,
});

/** One employee's document file. The same component is the employee record's HR tab; this screen
 * adds the roster picker. */
function HrEmployees() {
  const { facilityId } = useFacility();
  // Starting hiring files names people, not a building.
  const canManage = useCanWithoutBuilding("hr:manage");
  const canSeeStaff = useCanAnywhere("staff:read");
  /* The chosen person lives in the address, so a name clicked on a report,
     a refresh, or Back all land on the same file. */
  const { employeeId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const userId = employeeId ?? "";
  const choose = (next: string) => void navigate({ search: { employeeId: next }, replace: true });

  const employees = useQuery(
    orpc.administration.staff.list.queryOptions({
      input: {
        facilityId,
        includeInactive: false,
        kind: "employee",
        limit: PAGE_LIMIT_MAX,
        offset: 0,
      },
    }),
  );
  const [starting, setStarting] = useState(false);
  const everyone = (employees.data?.rows ?? []).map((employee) => employee.id);

  return (
    <>
      <PageHeader
        title="Employee documents"
        meta="What each person has to hold on file, and where each one stands."
        actions={
          /* The day-one case: a customer imports their staff and nobody has a hiring record, so the
           * job application and the offer letter can reach none of them. Idempotent — anyone who
           * already has one is left alone and not counted. */
          canManage && (employees.data?.rows.length ?? 0) > 0 ? (
            <Button variant="outline" onClick={() => setStarting(true)}>
              Start hiring files
            </Button>
          ) : null
        }
      />

      {employees.isError ? (
        <ReadFailed
          message="Could not load the employee list."
          error={employees.error}
          onRetry={() => void employees.refetch()}
          retrying={employees.isFetching}
        />
      ) : employees.data?.rows.length === 0 && userId === "" ? (
        <EmptyState
          title="No employees in this building yet"
          description="Add people on the Employees screen, then their document files start here."
          action={
            canSeeStaff ? (
              <Button variant="outline" asChild>
                <Link to="/hr">Open Employees</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-1.5">
          <Label htmlFor="hr-employee">Employee</Label>
          <Select value={userId} onValueChange={choose}>
            <SelectTrigger id="hr-employee" className="w-full sm:w-80">
              <SelectValue
                placeholder={employees.isPending ? "Loading people…" : "Choose an employee"}
              />
            </SelectTrigger>
            <SelectContent>
              {(employees.data?.rows ?? []).map((employee) => (
                <SelectItem key={employee.id} value={employee.id}>
                  {employee.jobTitle ? `${employee.name}, ${employee.jobTitle}` : employee.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {userId === "" ? (
        employees.data?.rows.length === 0 || employees.isError ? null : (
          <EmptyState
            title="Choose an employee"
            description="Their requirements and everything filed against them appear here."
          />
        )
      ) : (
        <EmployeeHrFile userId={userId} />
      )}
      <StartHiringFilesDialog userIds={everyone} open={starting} onOpenChange={setStarting} />
    </>
  );
}
